import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { characterClassApi } from '../../../api/adminApi';
import { useTranslation } from '../../../i18n/useTranslation';
import type { CharacterClassData } from '../../../types/adminTypes';

const ELEMENT_ICONS: Record<string, string> = {
  Fire: '🔥', Earth: '🌿', Air: '💨', Water: '💧', Neutral: '⚪',
};

const ATTR_ICONS: Record<string, string> = {
  strength: '💪', intelligence: '🧠', agility: '🏃', luck: '🍀', wisdom: '📖',
};

export function ClassesPage() {
  const navigate = useNavigate();
  const { t }    = useTranslation();

  const [classes, setClasses] = useState<CharacterClassData[]>([]);
  const [loading, setLoading] = useState(true);
  const [guideOpen, setGuideOpen] = useState(false);

  useEffect(() => {
    void characterClassApi.list()
      .then(setClasses)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleDelete = async (id: string) => {
    if (!confirm(t('classes.confirmDelete'))) return;
    try {
      await characterClassApi.delete(id);
      setClasses((prev) => prev.filter((c) => c.id !== id));
    } catch { /* handled by adminApi logger */ }
  };

  return (
    <>
      <div className="adminHeader" style={{ position: 'relative' }}>
        <h1>🛡️ {t('classes.title')}</h1>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <button 
            title={t('guide.attributes.btn')}
            onClick={() => setGuideOpen(true)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', fontSize: 28, padding: 0,
              transition: 'transform 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center',
              lineHeight: 1
            }}
            onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.1)'}
            onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
          >
            📚
          </button>
          <button className="btn btnPrimary" onClick={() => navigate('/admin/classes/new')}>
            ✨ {t('classes.create')}
          </button>
        </div>
      </div>

      {guideOpen && (
        <div className="modalOverlay" onClick={() => setGuideOpen(false)} style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.8)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          backdropFilter: 'blur(4px)'
        }}>
          <div className="modalContent" onClick={e => e.stopPropagation()} style={{
            background: 'var(--panel-bg)', border: '1px solid var(--border)',
            borderRadius: 12, width: '90%', maxWidth: 650, maxHeight: '85vh',
            overflowY: 'auto', padding: 32, position: 'relative', boxShadow: '0 10px 40px rgba(0,0,0,0.5)'
          }}>
            <button 
              onClick={() => setGuideOpen(false)}
              style={{ position: 'absolute', top: 16, right: 16, background: 'transparent', border: 'none', color: 'var(--text-dim)', fontSize: 24, cursor: 'pointer' }}
            >×</button>
            <h2 style={{ margin: '0 0 24px 0', color: 'var(--text)', borderBottom: '1px solid var(--border)', paddingBottom: 16 }}>
              📖 {t('guide.attributes.title')}
            </h2>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, color: 'var(--text-dim)', lineHeight: 1.6 }}>
              <div className="adminCard" style={{ padding: 16, borderLeft: '4px solid #FF5500' }}>
                <h3 style={{ margin: '0 0 8px 0', color: 'var(--text)' }}>{t('guide.attributes.strengthTitle')}</h3>
                <p style={{ margin: 0 }}>{t('guide.attributes.strengthDesc')}</p>
              </div>

              <div className="adminCard" style={{ padding: 16, borderLeft: '4px solid #00AAFF' }}>
                <h3 style={{ margin: '0 0 8px 0', color: 'var(--text)' }}>{t('guide.attributes.intTitle')}</h3>
                <p style={{ margin: 0 }}>{t('guide.attributes.intDesc')}</p>
              </div>

              <div className="adminCard" style={{ padding: 16, borderLeft: '4px solid #00DD88' }}>
                <h3 style={{ margin: '0 0 8px 0', color: 'var(--text)' }}>{t('guide.attributes.agiTitle')}</h3>
                <p style={{ margin: 0 }}>{t('guide.attributes.agiDesc')}</p>
              </div>

              <div className="adminCard" style={{ padding: 16, borderLeft: '4px solid #4466FF' }}>
                <h3 style={{ margin: '0 0 8px 0', color: 'var(--text)' }}>{t('guide.attributes.luckTitle')}</h3>
                <p style={{ margin: 0 }}>{t('guide.attributes.luckDesc')}</p>
              </div>

              <div className="adminCard" style={{ padding: 16, borderLeft: '4px solid #AA00FF' }}>
                <h3 style={{ margin: '0 0 8px 0', color: 'var(--text)' }}>📖 {t('guide.attributes.wisdomTitle')}</h3>
                <p style={{ margin: 0 }}>
                  {t('guide.attributes.wisdomDesc')}<br/>
                  <b>• Wisdom:</b> {t('guide.attributes.wisdomStat')}<br/>
                  <b>• Initiative:</b> {t('guide.attributes.initiativeStat')}<br/>
                  <b>• Block / Dodge:</b> {t('guide.attributes.blockDodgeStat')}
                </p>
              </div>
            </div>
            
            <div style={{ marginTop: 24, textAlign: 'right' }}>
              <button className="btn btnPrimary" onClick={() => setGuideOpen(false)}>{t('guide.attributes.understood')}</button>
            </div>
          </div>
        </div>
      )}

      <div className="adminContent">
        {loading ? (
          <div className="spinner" />
        ) : classes.length === 0 ? (
          <div className="adminCard" style={{ padding: 24, textAlign: 'center', color: 'var(--text-dim)' }}>
            {t('classes.empty')}
          </div>
        ) : (
          <table className="adminTable">
            <thead>
              <tr>
                <th>{t('classes.col.name')}</th>
                <th>{t('classes.col.slug')}</th>
                <th>{t('classes.col.element')}</th>
                <th>{t('classes.col.attribute')}</th>
                <th>HP</th>
                <th>AP</th>
                <th>MP</th>
                <th>{t('classes.col.spells')}</th>
                <th style={{ width: 120 }}>{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {classes.map((c) => (
                <tr key={c.id}>
                  <td style={{ fontWeight: 600 }}>{t(`class.${c.slug}` as any) || c.display_name}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{c.slug}</td>
                  <td>{ELEMENT_ICONS[c.primary_element] ?? ''} {t(`element.${c.primary_element}` as any) || c.primary_element}</td>
                  <td>{ATTR_ICONS[c.primary_attribute] ?? ''} {t(`attr.${c.primary_attribute}` as any) || c.primary_attribute}</td>
                  <td>{c.hp_max}</td>
                  <td>{c.ap_max}</td>
                  <td>{c.mp_max}</td>
                  <td>{c.spell_ids.length}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        className="btn btnSecondary"
                        style={{ fontSize: 11, padding: '4px 10px' }}
                        onClick={() => navigate(`/admin/classes/${c.id!}/edit`)}
                      >
                        ✏ {t('common.edit')}
                      </button>
                      <button
                        className="btn"
                        style={{ fontSize: 11, padding: '4px 10px', color: '#e74c3c', border: '1px solid #e74c3c', background: 'transparent' }}
                        onClick={() => void handleDelete(c.id!)}
                      >
                        ✕
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
